import { useState, useEffect, useCallback } from 'react'
import { Headphones, BarChart3, Brain } from 'lucide-react'
import { cn } from '@/lib/utils'

const FEATURES = [
    {
        icon: Headphones,
        label: 'Suivi des appels',
        description: 'Chaque appel analysé, classifié et scoré automatiquement.',
        image: '/preview-calls.webp',
    },
    {
        icon: BarChart3,
        label: 'Analytics avancés',
        description: 'KPIs en temps réel, tendances et performance globale.',
        image: '/preview-analytics.webp',
    },
    {
        icon: Brain,
        label: 'IA intégrée',
        description: 'Audit IA, résumé exécutif et recommandations actionnables.',
        image: '/preview-ai.webp',
    },
]

const AUTO_ROTATE_INTERVAL = 5000

export function FeatureShowcase({ mounted }: { mounted: boolean }) {
    const [activeIndex, setActiveIndex] = useState(0)
    const [isPaused, setIsPaused] = useState(false)
    const [imageLoaded, setImageLoaded] = useState(false)
    const [animKey, setAnimKey] = useState(0)

    // Auto-rotation
    useEffect(() => {
        if (isPaused) return
        const timer = setInterval(() => {
            setImageLoaded(false)
            setActiveIndex((prev) => (prev + 1) % FEATURES.length)
            setAnimKey((k) => k + 1)
        }, AUTO_ROTATE_INTERVAL)
        return () => clearInterval(timer)
    }, [isPaused])

    const handleSelect = useCallback((index: number) => {
        setIsPaused(true)
        setImageLoaded(false)
        setActiveIndex(index)
        setAnimKey((k) => k + 1)
        setTimeout(() => setIsPaused(false), 10000)
    }, [])

    const active = FEATURES[activeIndex]!

    if (!active) return null

    return (
        <div
            className={cn(
                "space-y-5 transition-all duration-700 delay-300",
                mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            )}
        >
            {/* Feature Pills */}
            <div className="flex flex-wrap gap-2">
                {FEATURES.map((feature, i) => (
                    <button
                        key={feature.label}
                        onClick={() => handleSelect(i)}
                        className={cn(
                            "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-400 cursor-pointer",
                            i === activeIndex
                                ? "border border-[hsl(213,80%,64%)]/40 bg-[hsl(213,80%,64%)]/10 text-white shadow-lg shadow-[hsl(213,80%,64%)]/10"
                                : "border border-white/10 bg-white/[0.04] backdrop-blur-md text-white/60 hover:text-white/80 hover:border-white/20 hover:bg-white/[0.07]"
                        )}
                    >
                        <feature.icon className={cn(
                            "w-4 h-4 transition-colors duration-300",
                            i === activeIndex ? "text-[hsl(213,80%,64%)]" : "text-[hsl(213,80%,64%)]/60"
                        )} />
                        {feature.label}
                    </button>
                ))}
            </div>

            {/* Preview Card */}
            <div className="relative rounded-2xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-xl overflow-hidden">
                {/* Progress bar for auto-rotate */}
                {!isPaused && (
                    <div className="absolute top-0 left-0 right-0 h-[2px] z-20">
                        <div
                            key={`progress-${animKey}`}
                            className="h-full bg-gradient-to-r from-[hsl(213,80%,64%)] to-[hsl(265,75%,68%)]"
                            style={{
                                animation: `progressBar ${AUTO_ROTATE_INTERVAL}ms linear infinite`,
                            }}
                        />
                    </div>
                )}

                {/* Screenshot preview — scrolling pan effect via translateY */}
                <div className="relative h-[220px] xl:h-[260px] overflow-hidden">
                    {/* Gradient overlay top */}
                    <div className="absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-black/40 to-transparent z-10 pointer-events-none" />
                    {/* Gradient overlay bottom */}
                    <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#080e1f] to-transparent z-10 pointer-events-none" />

                    {/* Image wrapper — the image is rendered at full width, natural height,
                         and translated upward by the panDown animation */}
                    <div
                        key={`pan-${activeIndex}-${animKey}`}
                        className="absolute inset-x-0 top-0"
                        style={{
                            animation: imageLoaded
                                ? `panDown 5s ease-in-out infinite alternate`
                                : 'none',
                        }}
                    >
                        <img
                            key={`img-${activeIndex}`}
                            src={active.image}
                            alt={active.label}
                            onLoad={() => setImageLoaded(true)}
                            className={cn(
                                "w-full transition-opacity duration-400",
                                imageLoaded ? "opacity-100" : "opacity-0"
                            )}
                            style={{ display: 'block' }}
                        />
                    </div>

                    {/* Loading shimmer */}
                    {!imageLoaded && (
                        <div className="absolute inset-0 bg-white/[0.03] animate-pulse" />
                    )}
                </div>

                {/* Description footer */}
                <div className="relative px-5 py-4 flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[hsl(213,80%,64%)]/10">
                        <active.icon className="w-4 h-4 text-[hsl(213,80%,64%)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-white/80 text-sm font-semibold font-['Fraunces'] leading-tight">
                            {active.label}
                        </p>
                        <p className="text-white/40 text-xs mt-0.5 leading-tight">
                            {active.description}
                        </p>
                    </div>
                    {/* Dot indicators */}
                    <div className="flex items-center gap-1.5">
                        {FEATURES.map((_, i) => (
                            <div
                                key={i}
                                className={cn(
                                    "rounded-full transition-all duration-300",
                                    i === activeIndex
                                        ? "w-5 h-1.5 bg-[hsl(213,80%,64%)]"
                                        : "w-1.5 h-1.5 bg-white/20 hover:bg-white/30 cursor-pointer"
                                )}
                                onClick={() => handleSelect(i)}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}
